// src/pages/terms.tsx
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Scale,
  FileText,
  Shield,
  ArrowUp,
  ChevronRight,
  Mail,
  MapPin,
} from "lucide-react";
import "../styles/pages/legal/legal-pages.css";

const TermsPage: React.FC = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const sections = [
    { id: "about", title: "About Orra Kingdom" },
    { id: "eligibility", title: "Eligibility" },
    { id: "platform-role", title: "Platform Role" },
    { id: "account-registration", title: "Account Registration" },
    { id: "church-affiliation", title: "Church & Community Affiliation" },
    { id: "listings", title: "Listings and Content" },
    { id: "transactions", title: "Transactions" },
    { id: "escrow", title: "Escrow and Payment Services" },
    { id: "fees", title: "Fees" },
    { id: "delivery", title: "Delivery and Fulfillment" },
    { id: "user-conduct", title: "User Conduct" },
    { id: "trust-scores", title: "Trust Scores and Reviews" },
    { id: "dispute-resolution", title: "Dispute Resolution" },
    { id: "fraud-prevention", title: "Fraud Prevention" },
    { id: "intellectual-property", title: "Intellectual Property" },
    { id: "third-party", title: "Third-Party Services" },
    { id: "liability", title: "Limitation of Liability" },
    { id: "indemnification", title: "Indemnification" },
    { id: "suspension", title: "Account Suspension" },
    { id: "data-protection", title: "Data Protection" },
    { id: "international", title: "International Use" },
    { id: "governing-law", title: "Governing Law" },
    { id: "changes", title: "Changes to Terms" },
    { id: "contact", title: "Contact Information" },
  ];

  return (
    <div className="legal-page-container">
      {/* Background Effects - Glassmorphism Layers */}
      <div className="legal-background">
        <div className="legal-glow legal-glow-1" />
        <div className="legal-glow legal-glow-2" />
        <div className="legal-glow legal-glow-3" />
      </div>

      <div className="legal-content-wrapper">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="legal-card"
        >
          {/* Header */}
          <div className="legal-header">
            <div className="legal-meta">
              <span className="legal-badge">
                <Scale size={14} />
                Terms of Service
              </span>
              <span className="legal-date">
                Last Updated: 23rd February 2026
              </span>
            </div>
            <h1 className="legal-title">Orra Kingdom Terms</h1>
            <p className="legal-subtitle">
              Governing access to and use of the Orra Kingdom platform
            </p>
          </div>

          {/* Table of Contents */}
          <div className="legal-toc">
            <div className="legal-toc-title">Table of Contents</div>
            <div className="legal-toc-grid">
              {sections.map((section, index) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="legal-toc-item"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById(section.id)?.scrollIntoView({
                      behavior: "smooth",
                    });
                  }}
                >
                  <ChevronRight size={12} />
                  <span>
                    {index + 1}. {section.title}
                  </span>
                </a>
              ))}
            </div>
          </div>

          {/* Introduction */}
          <div className="legal-section" id="introduction">
            <div className="legal-section-content">
              <div className="legal-highlight">
                <p>
                  <strong>Welcome to Orra Kingdom.</strong> By accessing,
                  registering for, or using the Platform, you agree to be
                  legally bound by these Terms. If you do not agree with these
                  Terms, you must not access or use the Platform.
                </p>
              </div>
            </div>
          </div>

          <div className="legal-divider" />

          {/* Section 1 - About Orra Kingdom */}
          <div className="legal-section" id="about">
            <div className="legal-section-header">
              <span className="legal-section-number">1</span>
              <h2 className="legal-section-title">About Orra Kingdom</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                Orra Kingdom Limited ("Orra Kingdom", "Company", "we", "our", or
                "us") is a private limited liability company duly incorporated
                under the laws of the Republic of Kenya.
              </p>
              <p className="legal-paragraph">
                The Platform operates as a digital ecosystem designed to
                facilitate discovery, collaboration, and commercial transactions
                among verified members of faith communities worldwide.
              </p>
              <p className="legal-paragraph">
                Although the Company is incorporated in Kenya and initial
                operations may commence within Kenya, the Platform is intended
                for participation by users, churches, organizations, and
                businesses across Africa and globally wherever legally
                permitted.
              </p>
            </div>
          </div>

          {/* Section 2 - Eligibility */}
          <div className="legal-section" id="eligibility">
            <div className="legal-section-header">
              <span className="legal-section-number">2</span>
              <h2 className="legal-section-title">Eligibility</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                You may use the Platform only if:
              </p>
              <ul className="legal-list">
                <li className="legal-list-item">
                  You are at least eighteen (18) years old or the legal age of
                  majority in your jurisdiction.
                </li>
                <li className="legal-list-item">
                  You possess legal capacity to enter binding agreements.
                </li>
                <li className="legal-list-item">
                  Your use of the Platform complies with applicable laws in your
                  country or region.
                </li>
              </ul>
              <p className="legal-paragraph">
                By registering, you represent and warrant that all information
                you provide is accurate and truthful.
              </p>
              <p className="legal-paragraph">
                The Company reserves the right to suspend or terminate accounts
                found to contain false or misleading information.
              </p>
            </div>
          </div>

          {/* Section 3 - Platform Role */}
          <div className="legal-section" id="platform-role">
            <div className="legal-section-header">
              <span className="legal-section-number">3</span>
              <h2 className="legal-section-title">Platform Role</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                Orra Kingdom provides technological infrastructure that enables
                users to:
              </p>
              <ul className="legal-list">
                <li className="legal-list-item">
                  Discover businesses and service providers.
                </li>
                <li className="legal-list-item">Offer products or services.</li>
                <li className="legal-list-item">
                  Request services or products.
                </li>
                <li className="legal-list-item">
                  Communally interact within verified faith networks.
                </li>
                <li className="legal-list-item">
                  Conduct structured transactions using Platform safeguards.
                </li>
              </ul>
              <p className="legal-paragraph">
                The Company is not a party to contracts formed between buyers
                and sellers unless expressly stated.
              </p>
              <p className="legal-paragraph">
                Except where required by law, Orra Kingdom does not manufacture
                goods, perform services, or guarantee outcomes of independent
                transactions between users.
              </p>
              <p className="legal-paragraph">
                Users remain independently responsible for their agreements.
              </p>
            </div>
          </div>

          {/* Section 4 - Account Registration */}
          <div className="legal-section" id="account-registration">
            <div className="legal-section-header">
              <span className="legal-section-number">4</span>
              <h2 className="legal-section-title">
                Account Registration and Verification
              </h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                To access certain features, users must create an account.
                Registration may require:
              </p>
              <ul className="legal-list">
                <li className="legal-list-item">Identity information.</li>
                <li className="legal-list-item">Contact verification.</li>
                <li className="legal-list-item">
                  Church or community affiliation confirmation.
                </li>
                <li className="legal-list-item">
                  Additional documentation for businesses or professional
                  services.
                </li>
              </ul>
              <p className="legal-paragraph">
                The Company may request additional verification at any time.
                Failure to complete verification may limit account
                functionality.
              </p>
              <p className="legal-paragraph">
                Users must maintain confidentiality of login credentials and are
                responsible for all activity occurring under their accounts.
              </p>
            </div>
          </div>

          {/* Section 5 - Church Affiliation */}
          <div className="legal-section" id="church-affiliation">
            <div className="legal-section-header">
              <span className="legal-section-number">5</span>
              <h2 className="legal-section-title">
                Church and Community Affiliation
              </h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                Certain Platform features rely upon verified affiliation with
                participating churches or faith communities.
              </p>
              <p className="legal-paragraph">
                Church participation does not create agency, partnership, or
                employment relationships between churches and the Company.
              </p>
              <p className="legal-paragraph">
                Churches do not control Platform operations unless expressly
                agreed in separate written agreements.
              </p>
              <p className="legal-paragraph">
                Users remain individually responsible for their commercial
                conduct.
              </p>
            </div>
          </div>

          {/* Section 6 - Listings */}
          <div className="legal-section" id="listings">
            <div className="legal-section-header">
              <span className="legal-section-number">6</span>
              <h2 className="legal-section-title">Listings and Content</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                Users may publish business listings, talent profiles, product
                catalogs, portfolios, and related materials.
              </p>
              <p className="legal-paragraph">
                By submitting content, you grant Orra Kingdom a worldwide,
                non-exclusive, royalty-free license to display, host, reformat,
                and promote such content.
              </p>
              <p className="legal-paragraph">Content must not:</p>
              <ul className="legal-list">
                <li className="legal-list-item">Violate laws.</li>
                <li className="legal-list-item">
                  Contain fraudulent representations.
                </li>
                <li className="legal-list-item">
                  Infringe intellectual property rights.
                </li>
                <li className="legal-list-item">
                  Promote harmful or unlawful activity.
                </li>
              </ul>
              <p className="legal-paragraph">
                The Company may remove listings at its discretion where
                necessary to maintain safety or compliance.
              </p>
            </div>
          </div>

          {/* Section 7 - Transactions */}
          <div className="legal-section" id="transactions">
            <div className="legal-section-header">
              <span className="legal-section-number">7</span>
              <h2 className="legal-section-title">Transactions</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                The Platform may facilitate transactions involving:
              </p>
              <ul className="legal-list">
                <li className="legal-list-item">Physical goods.</li>
                <li className="legal-list-item">Professional services.</li>
                <li className="legal-list-item">Digital deliverables.</li>
              </ul>
              <p className="legal-paragraph">
                Buyers and sellers agree upon scope, price, and delivery
                expectations through Platform tools.
              </p>
              <p className="legal-paragraph">
                Users acknowledge that Orra Kingdom facilitates transaction
                infrastructure but does not supervise execution of independent
                services.
              </p>
            </div>
          </div>

          {/* Section 8 - Escrow */}
          <div className="legal-section" id="escrow">
            <div className="legal-section-header">
              <span className="legal-section-number">8</span>
              <h2 className="legal-section-title">
                Escrow and Payment Services
              </h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                Where escrow or payment holding mechanisms are available, funds
                may be temporarily secured pending transaction completion.
                Release of funds may depend upon:
              </p>
              <ul className="legal-list">
                <li className="legal-list-item">Delivery confirmation.</li>
                <li className="legal-list-item">Milestone approvals.</li>
                <li className="legal-list-item">
                  Automated release timelines.
                </li>
              </ul>
              <p className="legal-paragraph">
                Payment processing may be conducted through licensed third-party
                financial service providers.
              </p>
              <p className="legal-paragraph">
                Users authorize the Company and its payment partners to process
                transactions necessary to operate the Platform.
              </p>
              <p className="legal-paragraph">
                The Company reserves rights to delay withdrawals or payments
                where fraud investigation or dispute review is required.
              </p>
            </div>
          </div>

          {/* Section 9 - Fees */}
          <div className="legal-section" id="fees">
            <div className="legal-section-header">
              <span className="legal-section-number">9</span>
              <h2 className="legal-section-title">Fees</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">The Platform may charge:</p>
              <ul className="legal-list">
                <li className="legal-list-item">
                  Transaction participation fees.
                </li>
                <li className="legal-list-item">Subscription fees.</li>
                <li className="legal-list-item">Promotional placement fees.</li>
              </ul>
              <p className="legal-paragraph">
                Fees will be disclosed before commitment. Failure to pay
                applicable fees may result in service suspension.
              </p>
              <p className="legal-paragraph">
                Fees are generally non-refundable unless required by law.
              </p>
            </div>
          </div>

          {/* Section 10 - Delivery */}
          <div className="legal-section" id="delivery">
            <div className="legal-section-header">
              <span className="legal-section-number">10</span>
              <h2 className="legal-section-title">Delivery and Fulfillment</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                Sellers remain responsible for fulfillment of goods or services.
                Where logistics coordination tools are provided, they exist
                solely as facilitation services.
              </p>
              <p className="legal-paragraph">
                The Company does not guarantee courier performance unless
                expressly stated.
              </p>
              <p className="legal-paragraph">
                Risk transfer occurs according to applicable delivery terms.
              </p>
            </div>
          </div>

          {/* Section 11 - User Conduct */}
          <div className="legal-section" id="user-conduct">
            <div className="legal-section-header">
              <span className="legal-section-number">11</span>
              <h2 className="legal-section-title">User Conduct</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">Users agree not to:</p>
              <ul className="legal-list">
                <li className="legal-list-item">Engage in fraud.</li>
                <li className="legal-list-item">
                  Circumvent Platform payments.
                </li>
                <li className="legal-list-item">Harass or threaten others.</li>
                <li className="legal-list-item">Upload harmful software.</li>
                <li className="legal-list-item">
                  Misrepresent qualifications.
                </li>
                <li className="legal-list-item">
                  Manipulate ratings or reviews.
                </li>
              </ul>
              <p className="legal-paragraph">
                Attempting to conduct transactions off-platform to avoid fees
                may result in suspension.
              </p>
            </div>
          </div>

          {/* Section 12 - Trust Scores */}
          <div className="legal-section" id="trust-scores">
            <div className="legal-section-header">
              <span className="legal-section-number">12</span>
              <h2 className="legal-section-title">Trust Scores and Reviews</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                The Platform may generate reputation indicators based on
                transaction performance. Users acknowledge such scores are
                algorithmic assessments.
              </p>
              <p className="legal-paragraph">
                The Company does not guarantee accuracy or specific business
                outcomes.
              </p>
              <p className="legal-paragraph">
                Manipulation of reviews or feedback systems is prohibited.
              </p>
            </div>
          </div>

          {/* Section 13 - Dispute Resolution */}
          <div className="legal-section" id="dispute-resolution">
            <div className="legal-section-header">
              <span className="legal-section-number">13</span>
              <h2 className="legal-section-title">
                Dispute Resolution Between Users
              </h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                The Platform may provide dispute assistance services including:
              </p>
              <ul className="legal-list">
                <li className="legal-list-item">Evidence review.</li>
                <li className="legal-list-item">Mediation facilitation.</li>
                <li className="legal-list-item">Arbitration processes.</li>
              </ul>
              <p className="legal-paragraph">
                Users agree that escrow decisions or arbitration outcomes made
                under Platform procedures may be binding to the extent permitted
                by law.
              </p>
              <p className="legal-paragraph">
                The Company reserves discretion when administering dispute
                outcomes.
              </p>
            </div>
          </div>

          {/* Section 14 - Fraud Prevention */}
          <div className="legal-section" id="fraud-prevention">
            <div className="legal-section-header">
              <span className="legal-section-number">14</span>
              <h2 className="legal-section-title">Fraud Prevention</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                Accounts may be suspended or transactions delayed where
                suspicious activity is detected.
              </p>
              <p className="legal-paragraph">
                Users agree to cooperate with investigations. Failure to
                cooperate may result in account termination.
              </p>
            </div>
          </div>

          {/* Section 15 - Intellectual Property */}
          <div className="legal-section" id="intellectual-property">
            <div className="legal-section-header">
              <span className="legal-section-number">15</span>
              <h2 className="legal-section-title">Intellectual Property</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                All Platform technology, trademarks, software, and branding
                remain property of Orra Kingdom Limited.
              </p>
              <p className="legal-paragraph">
                Users may not copy or reverse engineer Platform systems.
              </p>
              <p className="legal-paragraph">
                User content remains owned by the user subject to granted
                licenses.
              </p>
            </div>
          </div>

          {/* Section 16 - Third-Party Services */}
          <div className="legal-section" id="third-party">
            <div className="legal-section-header">
              <span className="legal-section-number">16</span>
              <h2 className="legal-section-title">Third-Party Services</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                The Platform may integrate third-party services including
                payment processors or logistics providers.
              </p>
              <p className="legal-paragraph">
                The Company is not responsible for independent third-party
                performance. Separate terms may apply.
              </p>
            </div>
          </div>

          {/* Section 17 - Limitation of Liability */}
          <div className="legal-section" id="liability">
            <div className="legal-section-header">
              <span className="legal-section-number">17</span>
              <h2 className="legal-section-title">Limitation of Liability</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                To the maximum extent permitted by law, Orra Kingdom shall not
                be liable for:
              </p>
              <ul className="legal-list">
                <li className="legal-list-item">Loss of profits.</li>
                <li className="legal-list-item">Business interruption.</li>
                <li className="legal-list-item">
                  Indirect or consequential damages.
                </li>
              </ul>
              <p className="legal-paragraph">
                Total liability shall not exceed fees paid by the user within
                the preceding twelve (12) months.
              </p>
              <p className="legal-paragraph">
                Nothing excludes liability where prohibited by law.
              </p>
            </div>
          </div>

          {/* Section 18 - Indemnification */}
          <div className="legal-section" id="indemnification">
            <div className="legal-section-header">
              <span className="legal-section-number">18</span>
              <h2 className="legal-section-title">Indemnification</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                Users agree to indemnify and hold harmless the Company against
                claims arising from:
              </p>
              <ul className="legal-list">
                <li className="legal-list-item">User misconduct.</li>
                <li className="legal-list-item">Breach of these Terms.</li>
                <li className="legal-list-item">
                  Violation of third-party rights.
                </li>
              </ul>
            </div>
          </div>

          {/* Section 19 - Account Suspension */}
          <div className="legal-section" id="suspension">
            <div className="legal-section-header">
              <span className="legal-section-number">19</span>
              <h2 className="legal-section-title">
                Account Suspension or Termination
              </h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                The Company may suspend or terminate accounts where necessary
                for:
              </p>
              <ul className="legal-list">
                <li className="legal-list-item">Fraud prevention.</li>
                <li className="legal-list-item">Legal compliance.</li>
                <li className="legal-list-item">Community safety.</li>
              </ul>
              <p className="legal-paragraph">
                Users may terminate accounts at any time. Certain obligations
                survive termination.
              </p>
            </div>
          </div>

          {/* Section 20 - Data Protection */}
          <div className="legal-section" id="data-protection">
            <div className="legal-section-header">
              <span className="legal-section-number">20</span>
              <h2 className="legal-section-title">Data Protection</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                User data is handled according to the Privacy Policy.
              </p>
              <p className="legal-paragraph">
                By using the Platform, you consent to processing necessary to
                operate services.
              </p>
            </div>
          </div>

          {/* Section 21 - International Use */}
          <div className="legal-section" id="international">
            <div className="legal-section-header">
              <span className="legal-section-number">21</span>
              <h2 className="legal-section-title">International Use</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                Users accessing the Platform outside Kenya remain responsible
                for compliance with local laws.
              </p>
              <p className="legal-paragraph">
                Certain features may not be available in all jurisdictions.
              </p>
            </div>
          </div>

          {/* Section 22 - Governing Law */}
          <div className="legal-section" id="governing-law">
            <div className="legal-section-header">
              <span className="legal-section-number">22</span>
              <h2 className="legal-section-title">Governing Law</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                These Terms shall be governed by the laws of the Republic of
                Kenya unless otherwise required by mandatory consumer protection
                laws in the user's jurisdiction.
              </p>
              <p className="legal-paragraph">
                Courts of competent jurisdiction in Kenya shall have primary
                authority over disputes relating to the Company.
              </p>
            </div>
          </div>

          {/* Section 23 - Changes to Terms */}
          <div className="legal-section" id="changes">
            <div className="legal-section-header">
              <span className="legal-section-number">23</span>
              <h2 className="legal-section-title">Changes to Terms</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                The Company may update these Terms periodically. Material
                changes will be communicated through reasonable notice.
              </p>
              <p className="legal-paragraph">
                Continued use constitutes acceptance.
              </p>
            </div>
          </div>

          {/* Section 24 - Contact Information */}
          <div className="legal-section" id="contact">
            <div className="legal-section-header">
              <span className="legal-section-number">24</span>
              <h2 className="legal-section-title">Contact Information</h2>
            </div>
            <div className="legal-section-content">
              <div className="legal-highlight">
                <p style={{ marginBottom: "1rem" }}>
                  <strong>Orra Kingdom Limited</strong>
                </p>
                <p
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    marginBottom: "0.5rem",
                  }}
                >
                  <MapPin size={16} />
                  <span>Nairobi, Republic of Kenya</span>
                </p>
                <p
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <Mail size={16} />
                  <a
                    href="mailto:hello@orrakingdom.com"
                    style={{
                      color: "inherit",
                      textDecoration: "underline",
                      textUnderlineOffset: "4px",
                    }}
                  >
                    hello@orrakingdom.com
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="legal-footer">
            <div className="legal-footer-info">
              © {new Date().getFullYear()} Orra Kingdom Limited. All rights
              reserved.
            </div>
            <div className="legal-footer-links">
              <Link to="/privacy" className="legal-footer-link">
                <Shield size={16} />
                Privacy Policy
              </Link>
              <Link to="/terms" className="legal-footer-link">
                <FileText size={16} />
                Terms
              </Link>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Back to Top Button */}
      <button
        className={`legal-back-to-top ${showBackToTop ? "visible" : ""}`}
        onClick={scrollToTop}
        aria-label="Back to top"
      >
        <ArrowUp size={20} />
      </button>
    </div>
  );
};

export default TermsPage;
